import { useState, useEffect, useRef } from 'react'
import { useAylaTimeout } from '~/components/shared/AylaTimeout'

const useAiStream = ({
  beforeStartStream = null,
  notEnoughData = false,
  error,
}) => {
  const [aiResponse, setAiResponse] = useState('')
  const [typingComplete, setTypingComplete] = useState(false)
  const [displayedText, setDisplayedText] = useState('')
  const [index, setIndex] = useState(0)
  const eventSource = useRef(null)
  const receivedResponse = useRef(false)
  const [responseState, setResponseState] = useState('not_started')

  const { timedOut, resetTimeout } = useAylaTimeout({
    waiting: responseState == 'running',
    activity: aiResponse,
  })

  const streamNotStarted = responseState == 'not_started'
  const streamRunning = responseState == 'running' && !timedOut
  const streamComplete = responseState == 'complete'
  const streamFailed =
    responseState == 'failed' || (responseState == 'running' && timedOut)

  const defaultErrorMessage =
    'I am unable to provide a summary currently because your resume does not contain enough information about your skills, experience, and education. Please add more details to your resume below and then try again.'

  const errorMessage = error || defaultErrorMessage

  useEffect(() => {
    if (!timedOut || !eventSource.current) return

    eventSource.current.close()
    eventSource.current = null
  }, [timedOut])

  useEffect(() => {
    const baseSpeed = 5
    const getSpeed = (char) => {
      if (char === '.' || char === '!' || char === ',') {
        return baseSpeed
      } else if (char === ' ') {
        return baseSpeed
      } else {
        return baseSpeed
      }
    }

    if (index < aiResponse.length) {
      setTypingComplete(false)
      const currentChar = aiResponse.charAt(index)
      const timeoutId = setTimeout(() => {
        setDisplayedText((prev) => prev + currentChar)
        setIndex(index + 1)
      }, getSpeed(currentChar))

      return () => clearTimeout(timeoutId)
    } else {
      setTypingComplete(true)
    }
  }, [index, setIndex, setTypingComplete, setDisplayedText, aiResponse])

  const resetResponse = () => {
    setDisplayedText('')
    setAiResponse('')
    setIndex(0)
    setResponseState('not_started')
    resetTimeout()
  }

  const startStream = async (eventUrl) => {
    if (typeof beforeStartStream == 'function') await beforeStartStream()

    setDisplayedText('')
    setAiResponse('')
    setIndex(0)
    resetTimeout()
    receivedResponse.current = false
    setResponseState('running')
    if (notEnoughData) {
      setAiResponse(errorMessage)
      setResponseState('complete')
    } else {
      const newSource = new EventSource(eventUrl)

      newSource.addEventListener('message', (event) => {
        if (event.data != 'null') {
          receivedResponse.current = true
          setAiResponse((r) => `${r}${event.data}`)
        }
      })

      newSource.addEventListener('error', (event) => {
        if (event.eventPhase === EventSource.CLOSED) {
          eventSource.current.close()
          eventSource.current = null
          setResponseState(receivedResponse.current ? 'complete' : 'failed')
        }
      })

      eventSource.current = newSource
    }
  }
  const value = {
    displayedText,
    typingComplete,
    streamNotStarted,
    streamRunning,
    streamComplete,
    streamFailed,
    resetResponse,
    startStream,
  }

  return value
}
export default useAiStream
